import { useState } from 'react';
import { societeApi } from '../../api/resources';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { OPTIONS_ST, grilleDuSecteur, prixPeriode } from '../../constants/plans';
import { MODULES } from '../reglages/SecteurCard.jsx';
import { C } from '../../theme/palette';

/** Rang de chaque taille dans la copie de la grille. */
const RANG_TAILLE = { petite: 0, moyenne: 1, grande: 2 };

/**
 * Ce que coûterait la formule actuelle dans la grille d'un autre module.
 *
 * <p>Le serveur recalcule le prix après le changement ; celui-ci l'annonce
 * avant, pour que le patron confirme en connaissance de cause. Il
 * reprend la formule, la taille et le Pack IA déjà souscrits.
 *
 * @param {object|null} abo abonnement porté par la société
 * @param {string} secteur module envisagé
 * @returns {{nom: string, mensuel: number, periode: number, annuel: boolean}|null}
 *          le tarif, ou null tant qu'aucune formule n'est choisie
 */
export function tarifDansModule(abo, secteur) {
  const plan = grilleDuSecteur(secteur).find((p) => p.id === abo?.formule);
  if (!plan) return null;
  const taille = OPTIONS_ST.entreprise[RANG_TAILLE[abo?.options?.taille]];
  const mensuel =
    plan.prix + (taille ? taille.prix : 0) + (abo?.options?.packIa ? OPTIONS_ST.packIA.prix : 0);
  const annuel = abo?.periodicite === 'ANNUEL';
  return { nom: plan.nom, mensuel, periode: prixPeriode({ prix: mensuel }, annuel), annuel };
}

/** Le seul module vers lequel on change en ligne. */
const MIX = 'mixte';

/**
 * Changer de module : une montée d'abonnement, jamais un simple clic.
 *
 * Le module fixe la grille tarifaire. Le changer passe donc par ici : le
 * nouveau tarif est annoncé, le patron confirme, et le serveur change module
 * et tarif d'un même geste. Les données ne bougent pas — passer au Mix ajoute
 * le magasin sans rien retirer au bâtiment.
 *
 * Seule la montée vers le Mix est proposée : une descente, ou le passage du
 * bâtiment au magasin, se règle avec nous, et le serveur la refuse.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.soc société, telle que l'écran la voit
 * @param {boolean} props.estMixte vrai si la société est mixte
 * @param {boolean} props.patron vrai si le compte peut engager l'entreprise
 * @param {(fiche: object) => void} [props.onModule] reçoit la fiche après le changement
 * @returns {import('react').ReactElement} la carte
 */
export function ChangementModule({ soc, estMixte, patron, onModule }) {
  // Une société mixte est vue, ecran par ecran, dans l'un de ses deux
  // univers : son module reste le Mix. Un Mix en deux sociétés aussi : chacune
  // porte son module, mais l'abonnement est celui du Mix.
  const abo = soc?.abonnement || null;
  const actuel =
    estMixte || soc?.secteur === 'mixte' || abo?.societeLiee ? 'mixte' : soc?.secteur || 'batiment';
  const [vise, setVise] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  const confirme = async () => {
    setErreur('');
    setEnvoi(true);
    try {
      const fiche = await societeApi.changeModule({ secteur: vise });
      setVise(null);
      if (typeof onModule === 'function') onModule(fiche);
    } catch (e) {
      setErreur(e?.message || "Le module n'a pas pu être changé.");
    } finally {
      setEnvoi(false);
    }
  };

  const avant = tarifDansModule(abo, actuel);
  const apres = vise ? tarifDansModule(abo, vise) : null;
  const unite = (t) => (t.annuel ? '.–/an HT' : '.–/mois HT');

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ padding: '14px 15px' }}>
        <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>Module</div>
        <div style={{ fontSize: 10, color: C.grey, marginBottom: 10 }}>
          Module actuel :{' '}
          <b style={{ color: C.ink }}>
            {MODULES[actuel][0]} {MODULES[actuel][1]}
          </b>
        </div>
        {!patron && (
          <div style={{ fontSize: 8.5, color: C.muted }}>
            Changer de module est réservé au patron : c&apos;est un changement d&apos;abonnement.
          </div>
        )}
        {patron && (
          <div style={{ display: 'flex', gap: 8 }}>
            {Object.entries(MODULES)
              .filter(([code]) => code === MIX && code !== actuel)
              .map(([code, [ic, nom]]) => (
                <button
                  key={code}
                  onClick={() => {
                    setErreur('');
                    setVise(code);
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 6px',
                    borderRadius: 10,
                    cursor: 'pointer',
                    fontSize: 10,
                    fontWeight: 800,
                    background: vise === code ? C.purple : C.navyXL,
                    color: vise === code ? '#fff' : C.ink,
                    border: '1px solid ' + (vise === code ? C.purple : C.border),
                  }}
                >
                  {ic} Passer au {nom}
                </button>
              ))}
          </div>
        )}
        {patron && (
          <div style={{ fontSize: 8.5, color: C.muted, marginTop: actuel === MIX ? 0 : 8 }}>
            Pour changer de module autrement que vers le Mix, contacte-nous.
          </div>
        )}
        {patron && vise && (
          <div
            style={{
              marginTop: 10,
              background: C.purpleL,
              borderRadius: 10,
              padding: '11px 12px',
              fontSize: 10,
              lineHeight: 1.6,
            }}
          >
            {apres ? (
              <div>
                Nouveau tarif : <b>{apres.nom}</b> {MODULES[vise][1]} à{' '}
                <b>
                  {apres.periode}
                  {unite(apres)}
                </b>
                {avant && (
                  <>
                    {' '}
                    (au lieu de {avant.periode}
                    {unite(avant)})
                  </>
                )}
              </div>
            ) : (
              <div>
                Grille du module {MODULES[vise][1]} : dès{' '}
                <b>{grilleDuSecteur(vise)[0].prix}.–/mois HT</b>. Ta formule se choisit ci-dessous.
              </div>
            )}
            <div style={{ color: C.grey, marginTop: 4 }}>
              Tes données sont conservées. Le nouveau tarif s&apos;applique dès la confirmation.
            </div>
            {erreur && (
              <div role="alert" style={{ color: C.red, fontWeight: 700, marginTop: 6 }}>
                {erreur}
              </div>
            )}
            <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
              <Btn label="Annuler" ghost color={C.grey} onClick={() => setVise(null)} />
              <div style={{ flex: 1 }}>
                <Btn
                  label={envoi ? 'Enregistrement…' : 'Confirmer le passage au ' + MODULES[vise][1]}
                  color={C.purple}
                  full
                  disabled={envoi}
                  onClick={confirme}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
